import React from 'react';

import PropTypes from 'prop-types';

import Form from '@/components/common/Form';
import formWidgets from '@/components/common/Form/widgets';
import Spinner from '@/components/common/Spinner';
import classes from '@/components/MarketplacePage/Service/style.module.scss';
import checkImage from '@/images/icons/check.svg';

const LinkIcon = () => (
  <svg className="size-5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

const DisconnectIcon = () => (
  <svg className="size-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
);

const CopyIcon = () => (
  <svg className="size-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
    <rect width="13" height="13" x="9" y="9" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const ApiKeyWidget = ({ id, value, options, disabled, readonly, onChange, onBlur, onFocus }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    options.onCopy?.(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="text"
        value={value || ''}
        disabled={disabled}
        readOnly={readonly}
        onChange={event => onChange(event.target.value)}
        onBlur={event => onBlur(id, event.target.value)}
        onFocus={event => onFocus(id, event.target.value)}
        className="min-w-0 flex-1"
      />
      <button
        type="button"
        onClick={handleCopy}
        disabled={!value}
        title={copied ? options.copiedTooltip : options.copyTooltip}
        aria-label={copied ? options.copiedTooltip : options.copyLabel}
        className="flex h-8 shrink-0 items-center justify-center rounded border border-gray-300 bg-white px-2 text-gray-600 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {copied ? options.copiedLabel : <CopyIcon />}
      </button>
    </div>
  );
};


const hasBlankApiKey = (schema, formData, type) => {
  if (type !== 'erp') return false;
  const hasApiKeyField = schema?.properties && ('apiKey' in schema.properties || 'api_key' in schema.properties);
  const isApiKeyBlank = !formData?.apiKey && !formData?.api_key;
  return hasApiKeyField && isApiKeyBlank;
};

const ServiceComponent = ({
  busy,
  formBusy,
  formData,
  isReparoFacil,
  isJetimob,
  schema,
  service,
  type,
  uiSchema,
  onChange,
  onDisableClick,
  onSubmit,
  onError,
  intl,
  // Dual Reparo Fácil Props
  marketplaceService,
  marketplaceFormData,
  onMarketplaceSubmit,
  onMarketplaceChange,
  onMarketplaceDisable,
  erpService,
  erpFormData,
  onErpSubmit,
  onErpChange,
  onErpDisable,
  onCopy,
}) => {
  const erpActive = erpService?.enabled || erpService?.status === 'ENABLED';

  const buttonLabel = hasBlankApiKey(schema, formData, type)
    ? intl.formatMessage({ id: 'reparoFacil.button.generateKey' })
    : intl.formatMessage({ id: 'SAVE' });

  const marketplaceSchema = {
    type: 'object',
    required: ['apiKey'],
    properties: {
      apiKey: {
        type: 'string',
        title: intl.formatMessage({ id: 'api_key', defaultMessage: 'Api Key' }),
      },
    },
  };

  const apiKeyOptions = {
    onCopy,
    copyLabel: intl.formatMessage({ id: 'marketplace.apiKey.copy', defaultMessage: 'Copiar' }),
    copiedLabel: intl.formatMessage({ id: 'marketplace.apiKey.copied', defaultMessage: 'Copiado' }),
    copyTooltip: intl.formatMessage({ id: 'marketplace.apiKey.copyTooltip', defaultMessage: 'Copiar API key' }),
    copiedTooltip: intl.formatMessage({ id: 'marketplace.apiKey.copiedTooltip', defaultMessage: 'API key copiada' }),
  };

  const getApiKeyUiSchema = (formSchema, baseUiSchema = {}) => ({
    ...baseUiSchema,
    ...(formSchema?.properties?.apiKey
      ? { apiKey: { ...(baseUiSchema.apiKey || {}), 'ui:widget': 'ApiKeyWidget', 'ui:options': { ...(baseUiSchema.apiKey?.['ui:options'] || {}), ...apiKeyOptions } } }
      : {}),
    ...(formSchema?.properties?.api_key
      ? { api_key: { ...(baseUiSchema.api_key || {}), 'ui:widget': 'ApiKeyWidget', 'ui:options': { ...(baseUiSchema.api_key?.['ui:options'] || {}), ...apiKeyOptions } } }
      : {}),
  });

  const apiKeyUiSchema = getApiKeyUiSchema(schema, uiSchema);

  const marketplaceApiKeyUiSchema = {
    apiKey: { 'ui:widget': 'ApiKeyWidget', 'ui:options': apiKeyOptions },
  };

  const isJetimobEnabled = Boolean(service?.enabled || service?.status === 'ENABLED');
  const jetimobApiKey = isJetimobEnabled
    ? formData?.apiKey || service?.data?.apiKey || service?.data?.api_key || ''
    : '';

  const instructionSteps = [
    intl.formatMessage({
      id: 'jetimob.instructions.step1',
      defaultMessage: 'No painel Jetimob, acesse Integrações no menu lateral.',
    }),
    intl.formatMessage({
      id: 'jetimob.instructions.step2',
      defaultMessage: 'No item Rede Vistorias, clique em Ativar integração.',
    }),
    intl.formatMessage({
      id: 'jetimob.instructions.step3',
      defaultMessage: 'Após clicar em Gerar API Key ao lado, copie a chave e cole na página de integração da Jetimob.',
    }),
  ];

  return (
    <div className={classes.service}>
      {busy ? (
        <div className={classes.spinnerWrapper}>
          <Spinner />
        </div>
      ) : isJetimob ? (
        <>
          {/* Service Header with Logo */}
          <div className={classes.info}>
            <div className={classes.image}>
              {service?.logo_url ? (
                <img src={service.logo_url} alt={service?.name || 'Jetimob'} />
              ) : (
                service?.name || 'Jetimob'
              )}
              {isJetimobEnabled && (
                <div className={classes.flag}>
                  <img src={checkImage} alt={intl.formatMessage({ id: 'ACTIVATED' })} />
                </div>
              )}
            </div>
            <div className={classes.data}>
              <h1 className={classes.name}>{service?.name || 'Jetimob'}</h1>
              <p className={classes.slogan}>{service?.slogan}</p>
              {isJetimobEnabled && (
                <div className={classes.status}>
                  <div className={classes.isEnabled}>{intl.formatMessage({ id: 'ACTIVATED' })}</div>
                </div>
              )}
            </div>
          </div>
          <hr />
          <p className={classes.description}>
            {service?.description ||
              intl.formatMessage({
                id: 'jetimob.description',
                defaultMessage: 'O sistema imobiliário do gestor de alta performance',
              })}
          </p>

          <div className="mt-4 flex flex-col gap-6 font-sans">
            {/* Card: Conectar conta Jetimob */}
            <div className="rounded-2xl border border-gray-200 bg-slate-50/50 p-6">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-200 overflow-hidden">
                  {service?.logo_url ? (
                    <img src={service.logo_url} alt={service?.name || 'Jetimob'} className="size-8 object-contain" />
                  ) : (
                    <LinkIcon />
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-bold text-gray-900">
                    {intl.formatMessage({ id: 'jetimob.title', defaultMessage: 'Conectar conta Jetimob' })}
                  </h2>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider ${
                      isJetimobEnabled
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-slate-200 text-slate-700'
                    }`}
                  >
                    {isJetimobEnabled
                      ? intl.formatMessage({ id: 'ACTIVATED', defaultMessage: 'Habilitado' })
                      : intl.formatMessage({ id: 'jetimob.inactive.status', defaultMessage: 'Não habilitado' })}
                  </span>
                </div>
              </div>

            {/* Grid Layout */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              {/* Instructions Column (lg:col-span-7) */}
              <div className="flex flex-col justify-between pr-0 lg:pr-6 lg:col-span-7">
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    {intl.formatMessage({
                      id: 'jetimob.instructions.title',
                      defaultMessage: 'Como integrar minha carteira de imóveis com a Jetimob?',
                    })}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {intl.formatMessage({
                      id: 'jetimob.instructions.subtitle',
                      defaultMessage: 'Gere uma API Key para permitir que o sistema externo da Jetimob faça requisições de vistorias.',
                    })}
                  </p>

                  <div className="mt-5 space-y-4">
                    {instructionSteps.map((stepText, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-slate-400 text-xs font-bold text-white">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-slate-700">{stepText}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Column (lg:col-span-5) */}
              <div className="flex flex-col lg:col-span-5">
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-bold text-gray-900">
                    {intl.formatMessage({
                      id: 'jetimob.accessKey.title',
                      defaultMessage: 'Gerar chave de acesso',
                    })}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {intl.formatMessage({
                      id: 'jetimob.accessKey.subtitle',
                      defaultMessage: 'Gere uma API Key para permitir que o sistema externo da Jetimob faça requisições.',
                    })}
                  </p>

                  <div className="mt-4">
                    <label className="mb-1.5 block text-xs font-bold text-slate-700">
                      {intl.formatMessage({ id: 'jetimob.accessKey.inputLabel', defaultMessage: 'API Key' })}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        disabled
                        value={jetimobApiKey}
                        placeholder={
                          isJetimobEnabled
                            ? ''
                            : intl.formatMessage({
                                id: 'jetimob.keyNotGenerated',
                                defaultMessage: 'Chave não gerada',
                              })
                        }
                        className="block h-10 w-full min-w-0 flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 text-xs text-gray-700 shadow-sm focus:outline-none"
                      />
                      <button
                        type="button"
                        disabled={!jetimobApiKey}
                        onClick={() => onCopy(jetimobApiKey)}
                        title={intl.formatMessage({
                          id: 'marketplace.apiKey.copyTooltip',
                          defaultMessage: 'Copiar API key',
                        })}
                        className="flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-slate-600 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>

                  <div className="mt-6">
                    {isJetimobEnabled ? (
                      <button
                        type="button"
                        disabled={formBusy}
                        onClick={onDisableClick}
                        className="flex h-10 w-full cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-600 shadow-sm transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      >
                        <DisconnectIcon />
                        {formBusy
                          ? intl.formatMessage({ id: 'LOADING', defaultMessage: 'Carregando...' })
                          : intl.formatMessage({
                              id: 'jetimob.accessKey.deactivate',
                              defaultMessage: 'Desativar Integração',
                            })}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={formBusy}
                        onClick={() =>
                          onSubmit({
                            formData: {
                              name: service?.name || 'Jetimob',
                              ...(formData?.hooks?.length ? { hooks: formData.hooks } : {}),
                            },
                          })
                        }
                        className="flex h-10 w-full cursor-pointer items-center justify-center rounded-lg border-0 bg-cyan-500 hover:bg-cyan-600 px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50"
                      >
                        <svg className="size-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {formBusy
                          ? intl.formatMessage({ id: 'LOADING', defaultMessage: 'Carregando...' })
                          : intl.formatMessage({ id: 'jetimob.accessKey.button', defaultMessage: 'Gerar API Key' })}
                      </button>
                    )}
                  </div>

                  <p className="mt-4 text-xs leading-relaxed text-slate-500">
                    {intl.formatMessage({
                      id: 'jetimob.accessKey.footer',
                      defaultMessage:
                        'Clique em "Gerar API Key" para gerar sua API Key. Em seguida, copie a chave gerada e insira no sistema da Jetimob para integrar.',
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
      ) : isReparoFacil ? (
        <div className="mt-4 flex flex-col gap-8 font-sans">
          {/* Card 1: Reparo Fácil no Painel RV */}
          {marketplaceService && (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              {/* Header */}
              <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-6">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                  <LinkIcon />
                </div>
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-bold text-gray-900">{intl.formatMessage({ id: 'reparoFacil.title.marketplace', defaultMessage: 'Reparo Fácil no Painel RV' })}</h2>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider ${
                      marketplaceService.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {marketplaceService.enabled
                      ? intl.formatMessage({ id: 'ACTIVATED' })
                      : intl.formatMessage({ id: 'reparoFacil.inactive.status' })}
                  </span>
                </div>
              </div>

              {/* Grid Layout */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                {/* Instructions Column */}
                <div className="flex flex-col justify-between lg:col-span-7 pr-0 lg:pr-6 border-r-0 lg:border-r border-slate-100">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">
                      {intl.formatMessage({ id: 'reparoFacil.instructions.connect.title' })}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      {intl.formatMessage({ id: 'reparoFacil.instructions.connect.subtitle' })}
                    </p>

                    <ol className="mt-5 space-y-4 list-none p-0 m-0">
                      <li className="flex items-start gap-3">
                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white mt-0.5">
                          1
                        </span>
                        <span className="text-sm text-slate-700">
                          {intl.formatMessage(
                            { id: 'reparoFacil.instructions.connect.step1' },
                            {
                              link: (
                                <a
                                  href="https://www.reparofacil.imb.br/"
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-600 font-semibold hover:underline"
                                >
                                  {intl.formatMessage({ id: 'reparoFacil.instructions.connect.step1.linkText' })}
                                </a>
                              ),
                            }
                          )}
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white mt-0.5">
                          2
                        </span>
                        <span className="text-sm text-slate-700">
                          {intl.formatMessage({ id: 'reparoFacil.instructions.connect.step2' })}
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white mt-0.5">
                          3
                        </span>
                        <span className="text-sm text-slate-700">
                          {intl.formatMessage({ id: 'reparoFacil.instructions.connect.step3' })}
                        </span>
                      </li>
                    </ol>
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-100">
                    <p className="text-xs italic text-slate-500">
                      {intl.formatMessage({ id: 'reparoFacil.instructions.connect.footer' })}
                    </p>
                  </div>
                </div>

                {/* Action Column */}
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <h3 className="text-sm font-bold text-slate-900 mb-1.5">
                    {intl.formatMessage({ id: 'reparoFacil.instructions.connect.active.title' })}
                  </h3>
                  <p className="text-xs text-slate-500 mb-6">
                    {intl.formatMessage({ id: 'reparoFacil.instructions.connect.subtitle' })}
                  </p>

                  {marketplaceService.enabled ? (
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-full rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                        {intl.formatMessage({
                          id: 'reparoFacil.marketplace.connected',
                          defaultMessage: 'Integração ativa. Sua conta Reparo Fácil está conectada ao Painel RV.',
                        })}
                      </div>

                      <button
                        type="button"
                        onClick={onMarketplaceDisable}
                        className="mt-2 w-full flex items-center justify-center rounded-lg bg-red-500 hover:bg-red-600 text-white px-4 py-2.5 text-xs font-bold transition-colors cursor-pointer shadow-sm border-0"
                      >
                        <DisconnectIcon />
                        {intl.formatMessage({ id: 'reparoFacil.disconnect', defaultMessage: 'Desconectar' })}
                      </button>
                    </div>
                  ) : (
                    <div className="[&_.form-group]:mb-4 [&_label]:block [&_label]:text-xs [&_label]:font-semibold [&_label]:text-slate-700 [&_label]:mb-1.5 [&_input]:w-full [&_input]:h-10 [&_input]:px-3 [&_input]:border [&_input]:border-slate-300 [&_input]:rounded-lg [&_input]:text-sm [&_input:focus]:outline-none [&_input:focus]:border-blue-500 [&_input:focus]:ring-2 [&_input:focus]:ring-blue-100">
                      <Form
                        formData={marketplaceFormData}
                        schema={marketplaceSchema}
                        uiSchema={marketplaceApiKeyUiSchema}
                        widgets={{ ...formWidgets, ApiKeyWidget }}
                        onSubmit={onMarketplaceSubmit}
                        onChange={onMarketplaceChange}
                        onError={onError}
                      >
                        <button
                          type="submit"
                          disabled={formBusy}
                          className="mt-6 flex h-10 w-full items-center justify-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:opacity-50 cursor-pointer border-0"
                        >
                          {intl.formatMessage({ id: 'SAVE' })}
                        </button>
                      </Form>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Card 2: Vistorias no Painel Reparo Fácil */}
          {erpService && (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              {/* Header */}
              <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-6">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                  <LinkIcon />
                </div>
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-bold text-gray-900">{intl.formatMessage({ id: 'reparoFacil.title.erp', defaultMessage: 'Vistorias no Painel Reparo Fácil' })}</h2>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider ${
                      erpActive
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {erpActive
                      ? intl.formatMessage({ id: 'ACTIVATED' })
                      : intl.formatMessage({ id: 'reparoFacil.inactive.status' })}
                  </span>
                </div>
              </div>

              {/* Grid Layout */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                {/* Instructions Column */}
                <div className="flex flex-col justify-between lg:col-span-7 pr-0 lg:pr-6 border-r-0 lg:border-r border-slate-100">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">
                      {intl.formatMessage({ id: 'reparoFacil.instructions.request.title' })}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      {intl.formatMessage({ id: 'reparoFacil.instructions.request.subtitle' })}
                    </p>

                    <ol className="mt-5 space-y-4 list-none p-0 m-0">
                      <li className="flex items-start gap-3">
                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white mt-0.5">
                          1
                        </span>
                        <span className="text-sm text-slate-700">
                          {intl.formatMessage({ id: 'reparoFacil.instructions.request.step1' })}
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white mt-0.5">
                          2
                        </span>
                        <span className="text-sm text-slate-700">
                          {intl.formatMessage({ id: 'reparoFacil.instructions.request.step2' })}
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white mt-0.5">
                          3
                        </span>
                        <span className="text-sm text-slate-700">
                          {intl.formatMessage(
                            { id: 'reparoFacil.instructions.request.step3' },
                            {
                              bold: (
                                <strong className="font-bold">
                                  {intl.formatMessage({ id: 'reparoFacil.instructions.request.step3.boldText' })}
                                </strong>
                              ),
                            }
                          )}
                        </span>
                      </li>
                    </ol>
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-100">
                    <p className="text-xs italic text-slate-500">
                      {intl.formatMessage({ id: 'reparoFacil.instructions.request.footer' })}
                    </p>
                  </div>
                </div>

                {/* Action Column */}
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <h3 className="text-sm font-bold text-slate-900 mb-1.5">
                    {intl.formatMessage({ id: 'reparoFacil.instructions.request.active.title' })}
                  </h3>
                  <p className="text-xs text-slate-500 mb-6">
                    {intl.formatMessage({ id: 'reparoFacil.instructions.request.subtitle' })}
                  </p>

                  {erpActive ? (
                    <div className="[&_.form-group]:mb-4 [&_label]:block [&_label]:text-xs [&_label]:font-semibold [&_label]:text-slate-700 [&_label]:mb-1.5 [&_input]:w-full [&_input]:h-10 [&_input]:px-3 [&_input]:border [&_input]:border-slate-300 [&_input]:rounded-lg [&_input]:text-sm [&_input:focus]:outline-none">
                      <Form
                        formData={erpFormData}
                        schema={erpService?.schema?.form || schema}
                        uiSchema={{ ...getApiKeyUiSchema(erpService?.schema?.form || schema, uiSchema), 'ui:readonly': true }}
                        widgets={{ ...formWidgets, ApiKeyWidget }}
                        onError={onError}
                      >
                        <button
                          type="button"
                          onClick={onErpDisable}
                          className="mt-4 w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-xs font-semibold text-gray-600 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 cursor-pointer shadow-sm"
                        >
                          {intl.formatMessage({ id: 'reparoFacil.active.deactivate' })}
                        </button>
                      </Form>
                    </div>
                  ) : (
                    <div className="[&_.form-group]:mb-4 [&_label]:block [&_label]:text-xs [&_label]:font-semibold [&_label]:text-slate-700 [&_label]:mb-1.5 [&_input]:w-full [&_input]:h-10 [&_input]:px-3 [&_input]:border [&_input]:border-slate-300 [&_input]:rounded-lg [&_input]:text-sm [&_input:focus]:outline-none [&_input:focus]:border-blue-500 [&_input:focus]:ring-2 [&_input:focus]:ring-blue-100">
                      <Form
                        formData={erpFormData}
                        schema={erpService?.schema?.form || schema}
                        uiSchema={getApiKeyUiSchema(erpService?.schema?.form || schema, uiSchema)}
                        widgets={{ ...formWidgets, ApiKeyWidget }}
                        onSubmit={onErpSubmit}
                        onChange={onErpChange}
                        onError={onError}
                      >
                        <button
                          type="submit"
                          disabled={formBusy}
                          className="mt-6 flex h-10 w-full items-center justify-center rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 cursor-pointer border-0"
                        >
                          <svg
                            className="size-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          {intl.formatMessage({ id: 'reparoFacil.button.generateKey' })}
                        </button>
                      </Form>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className={classes.info}>
            <div className={classes.image}>
              {service.logo_url ? <img src={service.logo_url} alt={service.name} /> : service.name}
              {(service.enabled || service.status === 'ENABLED') && (
                <div className={classes.flag}>
                  <img src={checkImage} alt={intl.formatMessage({ id: 'ACTIVATED' })} />
                </div>
              )}
            </div>
            <div className={classes.data}>
              <h1 className={classes.name}>{service.name}</h1>
              <p className={classes.slogan}>{service.slogan}</p>
              {(service.enabled || service.status === 'ENABLED') && (
                <div className={classes.status}>
                  <div className={classes.isEnabled}>{intl.formatMessage({ id: 'ACTIVATED' })}</div>
                  {(type === 'erp' || isReparoFacil) && (
                    <button type="button" className={classes.disable} onClick={onDisableClick}>
                      {intl.formatMessage({ id: 'DEACTIVATE' })}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <hr />
          <p className={classes.description}>{service.description}</p>
          {(type === 'erp' || !service.enabled) && (
            <div className={classes.formWrapper}>
              <Form
                formData={formData}
                schema={schema}
                uiSchema={apiKeyUiSchema}
                widgets={{ ...formWidgets, ApiKeyWidget }}
                onSubmit={onSubmit}
                onChange={onChange}
                onError={onError}
              >
                <button type="submit" className={classes.button} disabled={formBusy}>
                  {buttonLabel}
                </button>
              </Form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

ServiceComponent.propTypes = {
  busy: PropTypes.bool.isRequired,
  formBusy: PropTypes.bool.isRequired,
  formData: PropTypes.object.isRequired,
  isReparoFacil: PropTypes.bool,
  isJetimob: PropTypes.bool,
  schema: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  uiSchema: PropTypes.object,
  service: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onDisableClick: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  // Dual Reparo Fácil Props
  marketplaceService: PropTypes.object,
  marketplaceFormData: PropTypes.object,
  onMarketplaceSubmit: PropTypes.func,
  onMarketplaceChange: PropTypes.func,
  onMarketplaceDisable: PropTypes.func,
  erpService: PropTypes.object,
  erpFormData: PropTypes.object,
  onErpSubmit: PropTypes.func,
  onErpChange: PropTypes.func,
  onErpDisable: PropTypes.func,
  onCopy: PropTypes.func,
};

ServiceComponent.defaultProps = {
  isReparoFacil: false,
  isJetimob: false,
  service: null,
  marketplaceService: null,
  marketplaceFormData: {},
  onMarketplaceSubmit: () => {},
  onMarketplaceChange: () => {},
  onMarketplaceDisable: () => {},
  erpService: null,
  erpFormData: {},
  onErpSubmit: () => {},
  onErpChange: () => {},
  onErpDisable: () => {},
  onCopy: () => {},
};

export default ServiceComponent;
